









































































import {Vue, Component, Prop, Watch, Ref} from 'vue-property-decorator'
import * as d3 from 'd3'
import {Pose, PoseTimeline} from '@/store/pose'
import {KeyframeList, Keyframe, channelValue} from '@/keyframe'
import TimelineControlMenu from '@/components/TimelineControlMenu.vue'

interface KeyframeData {
  y: number;
  x: number;
  channel: number;
  index: number;
  id: string;
}

interface ChannelGraph {
  id: any;
  d: string;
}

interface PoseElement {
  x: number;
  y: number;
  idx: number;
}

/**
 * PoseTimeline: shows pose frames as icons on a time bar
 *
 * Emits update-time (local time), pose-changed
 */
@Component({
  components: {TimelineControlMenu},
  directives: {
    axis(el, binding) {
      const axis = binding.arg;
      if (axis !== 'x' && axis !== 'y')
        return
      const axisMethod = axis === 'x' ? 'axisBottom' : 'axisLeft'
      // binding.value should be this.axisArgs
      const methodArgs = binding.value[axis];
      const d3call = d3[axisMethod](methodArgs.scale).ticks(methodArgs.ticks)
      d3.select(el).call(d3call as any);
    }
  }
})
export default class KeyframeGraph extends Vue {

  @Prop({default: '100%'}) readonly width!: number | string
  @Prop({default: '5%'}) readonly height!: number | string
  @Prop() readonly totalTime!: number    // sec
  @Prop() readonly currentTime!: number  // sec
  @Prop() poseTimeline!: PoseTimeline
  @Prop() readonly inPoint!: number // sec
  @Prop() keyframeList!: KeyframeList
  @Prop() currentParamName!: string
  @Prop() expanded!: boolean
  @Ref() controlMenu!: any

  draggingPose = false
  draggingKey = false
  draggingCTI = false
  dragKey: Keyframe | null = null
  dragPose: Pose | null = null
  ctrlKey = false // is Ctrl key down? (restricts keyframe drag direction)
  dragChannel = 0
  poseHeight = 20
  poseWidth = 30

  // in SVG user units (which we scale 1:1 to pixels)
  margin = {left: 29, right: 20, top: 10, bottom: 25}

  container = {
    width: 0,
    height: 0,
    clientWidth: 0,
    clientHeight: 0,
  }

  resizeCb: any = null

  fullWidth = 0
  contentWidth = 0
  fullHeight = 0
  contentHeight = 0

  // scales time -> x coord (full)
  get linearXScale() {
    return d3.scaleLinear()
      .domain([0, this.totalTime])
      .range([this.margin.left, this.margin.left + this.contentWidth]);
  }

  // scales keyframe value -> y coord (full)
  get linearYScale() {
    let minVal = Number.MAX_VALUE
    let maxVal = Number.MIN_VALUE

    const kfs = this.keyframeList?.keyframes
    if (kfs && kfs.length > 0) {
      this.keyframeList.keyframes.forEach((k) => {
        let value = k.value
        if (typeof value === 'number')
          value = [value]

        minVal = Math.min(minVal, ...value)
        maxVal = Math.max(maxVal, ...value)
      })
      const margin = Math.max(0.15, 0.05 * (maxVal - minVal))
      minVal -= margin
      maxVal += margin
    } else {
      maxVal = 2
      minVal = 0
    }

    return d3.scaleLinear()
      .domain([maxVal, minVal])
      .range([this.margin.top, this.margin.top + this.contentHeight]);
  }

  get rawDataPoints(): KeyframeData[][] {
    if (this.keyframeList) {
      const result = []
      for (let c = 0; c < this.keyframeList.dim; c++) {
        result.push(this.keyframeList.keyframes.map(
          (k, index) => {
            return {x: k.time,
                    y: channelValue(k.value, c),
                    channel: c,
                    index: index,
                    id: c + "." + index}
          }))
      }
      return result
    } else {
      return []
    }
  }

  get channelGraphs(): ChannelGraph[] {
    if (!this.keyframeList || this.keyframeList.getNumKeyframes() === 0)
      return []

    interface XYData {
      x: number;
      y: number;
    }

    const result: ChannelGraph[] = []
    for (let c = 0; c < this.keyframeList.dim; c++) {
      const data: XYData[] = []
      // Subdivide into even time slices and sample value at each point
      const step = this.totalTime / 1000
      for (let t = 0; t < this.totalTime; t += step) {
        data.push({x: t,
                   y: channelValue(this.keyframeList.getValueAtTime(t), c)});
      }
      const lineGenerator = d3.line<XYData>()
        .x(d => this.linearXScale(d['x']))
        .y(d => this.linearYScale(d['y']))
        .curve(d3.curveLinear);
      const d = lineGenerator(data)
      if (d)
        result.push({id: c, d: d})
    }
    return result
  }

  get points(): KeyframeData[] {
    return this.rawDataPoints.flat().map(
      (pt) => {
        return {...pt, x: this.linearXScale(pt.x), y: this.linearYScale(pt.y)}
      })
  }

  // returns ticks and scale for each axis
  get axisArgs() {
    const xTickSpacingPixels = 100
    const yTickSpacingPixels = 20
    const nTicksX = this.contentWidth / xTickSpacingPixels // approx
    const nTicksY = this.contentHeight / yTickSpacingPixels // approx

    return {x: {ticks: nTicksX, scale: this.linearXScale},
            y: {ticks: nTicksY, scale: this.linearYScale}}
  }

  get poseElements() {
    const result: PoseElement[] = []
    for (const [idx, pose] of this.poseTimeline.poses.entries()) {
      const poseElement = {x: this.linearXScale(pose.time),
                           y: this.linearYScale(0.5), // center
                           idx: idx,
                           pose: pose}
      result.push(poseElement)
    }
    return result
  }

  get currentTimeIndicator() {
    // current time indicator -- will be interpolated into a <g> group element.
    // This renders the CTI directly at the current time.
    // Alternatively, the CTI could be rendered statically in the template at t=0
    // as a path, and we could just adjust its transform here.
    return `<line x1=${this.linearXScale(this.currentTime)}
                  y1=${2}
                  x2=${this.linearXScale(this.currentTime)}
                  y2=${this.fullHeight - 2}
                  stroke="yellow"
                  stroke-width="3"
                  etype="CTI"
             />`
  }

  setupGraph() {
    // I think we need the actual SVG DOM element here to get its clientWidth
    // (and have to update this on resize)
    const svgElement = document.getElementById('pose-timeline-svg')
    if (!svgElement)
      return

    // default user coord sys is CSS px, origin top left, size width x height
    // don't need to specify viewBox if it's 1:1 (nor preserveAspectRatio)
    // .attr('viewBox', `0 0 ${fullWidth} ${fullHeight}`)
    // .attr('preserveAspectRatio','xMinYMin')

    this.fullWidth = svgElement.clientWidth
    this.contentWidth = this.fullWidth - this.margin.left - this.margin.right
    this.fullHeight = svgElement.clientHeight
    this.contentHeight = this.fullHeight - this.margin.top - this.margin.bottom
    // console.log('Setting graph dims; content=', this.contentWidth, this.contentHeight,
    //            'from w/h=', this.width, this.height,
    //            'full=', this.fullWidth, this.fullHeight)
  }

  mounted() {
    this.setupGraph()
    window.addEventListener('resize', this.setupGraph)
  }

  @Watch('expanded')
  onExpandedChange() {
    Vue.nextTick(this.setupGraph)
  }

  startDrag(event: MouseEvent) {
    const target = event.target as HTMLElement

    // Get values (strings)
    const elementType = target.getAttributeNS(null, 'etype')
    const index = target.getAttributeNS(null, 'index') // keyframe index
    const channel = target.getAttributeNS(null, 'channel') // which channel of keyframe (e.g. 0=x, 1=y)
    // console.log(`Dragging ${target} elt type ${elementType}, index=${index}, ch ${channel}`)

    this.ctrlKey = event.ctrlKey

    if (target) {
      if (elementType === 'CTI' || elementType === null) {
        // CTI
        this.draggingCTI = true
        document.body.style.cursor = "e-resize"
        this.handleDrag(event)
      } else if (elementType === 'pose' && index) {
        this.draggingPose = true
        this.dragPose = this.poseTimeline.poses[Number(index)]
        document.body.style.cursor = "e-resize"
        this.$emit('startDrag')
      } else if (elementType === 'keyframe' && index) {
        this.draggingKey = true
        this.dragKey = this.keyframeList.getKeyframe(Number(index))
        this.dragChannel = Number(channel)
        if (!this.ctrlKey)
          document.body.style.cursor = "e-resize"
        this.$emit('startDrag')
      } else {
        // not over anything interesting, ignore
      }
    }
  }

  clampTime(time: number) {
    time = Math.round(time * 10) * 0.1 // to nearest 0.1 sec
    if (time < 0)
      time = 0
    if (time > this.totalTime)
      time = this.totalTime
    return time
  }

  handleDrag(event: MouseEvent) {
    if (event.button === 0) {
      const svgElement = event.target as unknown as SVGGraphicsElement
      const ctm = svgElement && svgElement.getScreenCTM()
      if (!ctm)
        return

      const x = (event.clientX - ctm.e) / ctm.a
      const y = (event.clientY - ctm.f) / ctm.d
      let time = this.linearXScale.invert(x)
      const value = this.linearYScale.invert(y)

      if (this.draggingCTI) {
        this.$emit('update-time', time + this.inPoint)
      } else if (this.keyframeList && this.draggingKey && this.dragKey) {
        time = this.clampTime(time)
        if (typeof this.dragKey.value === 'number') {
          this.$emit('keyframe-changed', {kf: this.dragKey, time, value})
        } else {
          const newValue = this.dragKey.value.slice()
          // vector param: slide in time only, don't change value -- unless ctrl key
          if (this.ctrlKey)
            newValue[this.dragChannel] = value
          this.$emit('keyframe-changed', {kf: this.dragKey, time, value: newValue})
        }
      } else if (this.poseTimeline && this.draggingPose && this.dragPose) {
        time = this.clampTime(time)
        this.$emit('pose-changed', {pose: this.dragPose, time})
        this.$emit('update-time', time)
      }
    } else {
      // Update mouse hover helper
    }
  }

  endDrag(event: MouseEvent) {
    // Update for final mouse position
    this.handleDrag(event)
    document.body.style.cursor = "default"
    this.draggingPose = false
    this.draggingKey = false
    this.draggingCTI = false
    this.ctrlKey = false
    this.snapCtiToPose()
    if (event.button != 2)      // don't close when trying to move to the control menu
      this.controlMenu.close()
  }

  endDragIfDragging(event: MouseEvent) {
    if (event.buttons)
      this.endDrag(event)
  }

  snapCtiToPose() {
    const nearestPoseTime = this.poseTimeline.getNearestPose(this.currentTime)?.time
    this.$emit('update-time', nearestPoseTime)
  }

  openControlMenu($event: MouseEvent) {
    if (this.controlMenu) {
      const target = $event.target as HTMLElement
      const elementType = target.getAttributeNS(null, 'etype')
      this.controlMenu.open($event, elementType)
    }
  }

  addPose(payload: {target: EventTarget | null; x: number}) {
    const svgElement = payload.target as unknown as SVGGraphicsElement
    const ctm = svgElement && svgElement.getScreenCTM()
    if (!ctm)
      return
    const xPos = (payload.x - ctm.e) / ctm.a
    const time = this.linearXScale.invert(xPos)
    this.$emit('add-pose', {time})
  }

  removePose(payload: {target: EventTarget | null; x: number}) {
    const target = payload.target as HTMLElement
    const elementType = target.getAttributeNS(null, 'etype')
    const index = target.getAttributeNS(null, 'index') // keyframe index

    if (elementType === 'pose') {
      const pose = this.poseTimeline.poses[Number(index)]
      this.$emit('delete-pose', {pose: pose})
    }
  }
}
